import * as React from "react";
import {
    Stack,
    TextField,
    TextFieldProps,
    Typography,
} from "@mui/material";
import {
    AdvancedSavedSearchItem,
    KeyValue,
} from "../../../services/types/enterpriseCoachingApiTypes";
import AdvancedSearchSingleSelect from "../../resources/cclResources/advancedSearchSingleSelect";
import { DatePicker } from "@mui/x-date-pickers";
import { useGetSessionOptionsQuery} from "../../../services/cclTokenedSessionApi";
import { useGetSharedAdminGroupsQuery } from "../../../services/cclTokenedAssessmentApi";

interface SessionSearchCardProps {
    updateCurrentSearch: (search: any) => void;
    pastSearch: AdvancedSavedSearchItem;
}

export interface AttributeSearch {
    attributeType: AutocompleteItem | undefined;
    attributeValue: AutocompleteItem | undefined;
    attributeLevel: AutocompleteItem | undefined;
    id: string;
}

export interface AutocompleteItem {
    id: string;
    label: string;
}

const searchInputSx = {
    "& .MuiOutlinedInput-input": {
        paddingTop: "8px!important",
        paddingBottom: "8px!important",
    },
    "& .MuiInputLabel-root": {
        marginTop: "-8px!important",
    },
    "& .MuiInputLabel-shrink": {
        marginTop: "auto!important",
    },
    flexGrow: 1,
};

const SessionSearchCard: React.FC<SessionSearchCardProps> = (props) => {

    const [title, setTitle] = React.useState<string>(props.pastSearch?.session?.title ? props.pastSearch.session.title.label: "");
    const [customer, setCustomer] = React.useState<string>(props.pastSearch?.session?.customer ? props.pastSearch.session.customer.label : "");
    const [startDateFrom, setStartDateFrom] = React.useState<Date | null>(props.pastSearch?.session?.startDateFrom ? new Date(props.pastSearch.session.startDateFrom.label) : null);
    const [startDateTo, setStartDateTo] = React.useState<Date | null>(props.pastSearch?.session?.startDateTo ? new Date(props.pastSearch.session.startDateTo.label) : null);
    const [projectId, setProjectId] = React.useState<string>(props.pastSearch?.session?.projectId ? props.pastSearch.session.projectId.label : "");
    const [sessionId, setSessionId] = React.useState<string>(props.pastSearch?.session?.sessionId ? props.pastSearch.session.sessionId.label : "");
    const [adminEmail, setAdminEmail] = React.useState<string>(props.pastSearch?.session?.administratorEmail ? props.pastSearch.session.administratorEmail.label : "");
    
    const {data: advancedSearchOptions} = useGetSessionOptionsQuery();
    const { data: sharedAdminGroups } = useGetSharedAdminGroupsQuery();

    const typeOptions = advancedSearchOptions ? advancedSearchOptions.sessionTypes.map((st:any) => ({id: st.val, label: st.option})) : [];
    const locationOptions = advancedSearchOptions ? advancedSearchOptions.managingLocations.map((ml:any) => ({id: ml.val, label: ml.option})) : [];
    const statusOptions = advancedSearchOptions ? advancedSearchOptions.sessionStatuses.map((sst:any) => ({id: sst.val, label: sst.option})) : [];
    const adminGroupOptions = sharedAdminGroups
        ? [...sharedAdminGroups]
              .sort((a, b) => a.groupName.localeCompare(b.groupName))
              .map((g) => ({ id: g.groupId.toString(), label: g.groupName }))
        : [];

    const handleTypeChange = (type: any) => {
        let temp = props.pastSearch;
        if (temp) {
            temp.session.type = mapIdToLabel(type, typeOptions)[0];
            props.updateCurrentSearch(temp);
        }
    }

    const handleStatusChange = (status: any) => {
        let temp = props.pastSearch;
        if (temp) {
            temp.session.status = mapIdToLabel(status, statusOptions)[0];
            props.updateCurrentSearch(temp);
        }
    }

    const handleLocationChange = (location: any) => {
        let temp = props.pastSearch;
        if (temp) {
            temp.session.managingLocation = mapIdToLabel(location, locationOptions)[0];
            props.updateCurrentSearch(temp);
        }
    }

    const handleAdminGroupChange = (group: any) => {
        let temp = props.pastSearch;
        if (temp) {
            temp.session.administratorGroup = mapIdToLabel(group, adminGroupOptions)[0];
            props.updateCurrentSearch(temp);
        }
    }

    const handleTitleChange = (title: string) => {
        setTitle(title);
        let temp = props.pastSearch;
        temp.session.title = { id: title, label: title };
        props.updateCurrentSearch(temp);
    }

    const handleCustomerChange = (customer: string) => {
        setCustomer(customer);
        let temp = props.pastSearch;
        temp.session.customer = { id: customer, label: customer };
        props.updateCurrentSearch(temp);
    }

    const handleAdminEmailChange = (email: string) => {
        setAdminEmail(email);
        let temp = props.pastSearch;
        temp.session.administratorEmail = { id: email, label: email };
        props.updateCurrentSearch(temp);
    }

    const handleProjectIdChange = (projectId: string) => {
        setProjectId(projectId);
        let temp = props.pastSearch;
        temp.session.projectId = { id: projectId, label: projectId };
        props.updateCurrentSearch(temp);
    }

    const handleSessionIdChange = (sessionId: string) => {
        setSessionId(sessionId);
        let temp = props.pastSearch;
        temp.session.sessionId = { id: sessionId, label: sessionId };
        props.updateCurrentSearch(temp);
    }

    const handleDateFromChange = (date: Date | null) => {
        setStartDateFrom(date);
        let temp = props.pastSearch;
        if (date === null || isNaN(date.getTime())) {
            temp.session.startDateFrom = undefined;
            props.updateCurrentSearch(temp);
            return;
        }
        temp.session.startDateFrom = { id: date?.toISOString(), label: date?.toLocaleDateString() };
        props.updateCurrentSearch(temp);
    }

    const handleDateToChange = (date: Date | null) => {
        setStartDateTo(date);
        let temp = props.pastSearch;
        if (date === null || isNaN(date.getTime())) {
            temp.session.startDateTo = undefined;
            props.updateCurrentSearch(temp);
            return;
        }        
        temp.session.startDateTo = { id: date?.toISOString(), label: date?.toLocaleDateString() };
        props.updateCurrentSearch(temp);
    }

    const mapIdToLabel = (id: number[] | string[], map: KeyValue[]) => {
        let values: KeyValue[] = [];
        for (let i = 0; i < id.length; i++) {
            //eslint-disable-next-line
            let label = map.find((x) => x.id == id[i]);
            if (label) {
                values.push(label);
            }
        }
        return values;
    };

    return (

        <Stack sx={{ m: 1, mt: 2 }} spacing={4}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Session
            </Typography>
            <AdvancedSearchSingleSelect
                dataList={typeOptions}
                id="type"
                label="Type"
                handleSelectionChanged={handleTypeChange}
                defaultValue={
                    props.pastSearch?.session?.type ? props.pastSearch.session.type : null
                }
            />
            <AdvancedSearchSingleSelect
                dataList={statusOptions}
                id="status"
                label="Status"
                handleSelectionChanged={handleStatusChange}
                defaultValue={
                    props.pastSearch?.session?.status ? props.pastSearch.session.status : null
                }
            />
            <TextField
                size="small"
                id="project-id"
                label="Project ID"
                variant="outlined"
                onChange ={(e)=> {handleProjectIdChange(e.target.value)}}
                fullWidth
                value={projectId}
            />
            <TextField
                size="small"
                id="session-id"
                label="Session ID"
                variant="outlined"
                onChange ={(e)=> {handleSessionIdChange(e.target.value)}}
                fullWidth
                value={sessionId}
            />
            <TextField
                size="small"
                id="title"
                label="Title"
                variant="outlined"
                onChange ={(e)=> {handleTitleChange(e.target.value)}}
                fullWidth
                value={title}
            />
            <TextField
                size="small"
                id="customer"
                label="Customer"
                variant="outlined"
                onChange ={(e)=> {handleCustomerChange(e.target.value)}}
                fullWidth
                value={customer}
            />
            <AdvancedSearchSingleSelect
                dataList={adminGroupOptions}
                id="administratorGroup"
                label="Administrator Group"
                handleSelectionChanged={handleAdminGroupChange}
                defaultValue={
                    props.pastSearch?.session?.administratorGroup ? props.pastSearch.session.administratorGroup : null
                }
                disabled={!!adminEmail.trim()}
            />
            <TextField
                size="small"
                id="administrator-email"
                label="Administrator Email"
                variant="outlined"
                onChange={(e) => { handleAdminEmailChange(e.target.value) }}
                fullWidth
                value={adminEmail}
                disabled={!!props.pastSearch?.session?.administratorGroup}
            />
            <AdvancedSearchSingleSelect
                dataList={locationOptions}
                id="Location"
                label="Managing Location"
                handleSelectionChanged={handleLocationChange}
                defaultValue={
                    props.pastSearch?.session?.managingLocation ? props.pastSearch.session.managingLocation : null
                }
            />
            <DatePicker
                label={"Start Date From"}
                value={startDateFrom}
                onChange={(newValue: Date | null) => {handleDateFromChange(newValue)}}
                renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                    params.fullWidth = true;
                    return (
                        <TextField
                            {...params}
                            onKeyDown={(e) => {}}
                            sx={searchInputSx}
                        />
                    );
                }}
            />
            <DatePicker
                label={"Start Date To"}
                value={startDateTo}
                onChange={(newValue: Date | null) => {handleDateToChange(newValue)}}
                renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                    params.fullWidth = true;
                    return (
                        <TextField
                            {...params}
                            onKeyDown={(e) => {}}
                            sx={searchInputSx}
                        />
                    );
                }}
            />
        </Stack>
    );
};

export default SessionSearchCard;